import React, { useMemo } from "react";
import moment from "moment";
import { useProductionData } from "../hooks/useProductionData";
import { routeDurationSec, routeProgressLabel } from "../utils";
import Spinner from "./Spinner";

function expectedUnits(durationSec, job) {
    const routeSec = routeDurationSec(job);
    if (!routeSec || !durationSec) return null;
    return Math.floor(durationSec / routeSec);
}

function barColor(actual, expected) {
    if (expected == null) return "#adb5bd";
    const pct = actual / expected;
    if (pct > 1.0)  return "#ffc107"; // over — gold (bar is striped anyway)
    if (pct >= 0.9) return "#28a745"; // on track
    if (pct >= 0.6) return "#fd7e14"; // behind
    return "#dc3545";                 // well behind
}

function SignalRow({ row }) {
    const exp = expectedUnits(row.effectiveDurSec, row.job);
    const actual = row.actual;
    const noData = actual === null;
    const pct = exp != null && exp > 0 && !noData ? Math.min((actual / exp) * 100, 100) : 0;
    const isOver = exp != null && !noData && actual > exp;
    const color = noData ? "#adb5bd" : barColor(actual, exp);
    const dimText = isOver ? "#856404" : "#6c757d";
    const boldText = isOver ? "#533f03" : "#343a40";

    return (
        <div
            style={isOver ? {
                background: "rgba(255,193,7,0.10)",
                border: "1px solid rgba(255,193,7,0.45)",
                borderRadius: 7,
                padding: "9px 11px 7px 11px",
            } : {
                paddingLeft: 4,
            }}
        >
            {/* Over-target banner */}
            {isOver && (
                <div className="d-flex align-items-center mb-2" style={{ gap: 5 }}>
                    <i className="fa fa-star" style={{ color: "#e6a800", fontSize: "0.75rem" }} />
                    <i className="fa fa-star" style={{ color: "#e6a800", fontSize: "0.75rem" }} />
                    <i className="fa fa-star" style={{ color: "#e6a800", fontSize: "0.75rem" }} />
                    <span style={{
                        color: "#856404",
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        letterSpacing: "0.07em",
                        textTransform: "uppercase",
                        margin: "0 4px",
                    }}>
                        Target Exceeded
                    </span>
                    <i className="fa fa-star" style={{ color: "#e6a800", fontSize: "0.75rem" }} />
                    <i className="fa fa-star" style={{ color: "#e6a800", fontSize: "0.75rem" }} />
                    <i className="fa fa-star" style={{ color: "#e6a800", fontSize: "0.75rem" }} />
                </div>
            )}

            {/* Label row */}
            <div className="d-flex align-items-baseline justify-content-between mb-1" style={{ gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                    {/* Signal pill */}
                    {row.signalName && (
                        <span style={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            color: isOver ? "#856404" : "#495057",
                            background: isOver ? "rgba(255,193,7,0.22)" : "#e9ecef",
                            borderRadius: 3,
                            padding: "1px 6px",
                            marginRight: 8,
                        }}>
                            <i
                                className={`fa ${row.isDigital ? "fa-toggle-on" : "fa-chart-line"} mr-1`}
                                style={{ fontSize: "0.6rem" }}
                            />
                            {row.signalName}
                        </span>
                    )}
                    {row.partName && (
                        <span style={{ fontSize: "0.78rem", color: boldText }}>
                            <i className="fa fa-cube mr-1" style={{ fontSize: "0.65rem", color: dimText }} />
                            {row.partName}
                        </span>
                    )}
                    {row.progressLabel && (
                        <span style={{ fontSize: "0.72rem", color: dimText, marginLeft: 4 }}>
                            · {row.progressLabel}
                        </span>
                    )}
                    {!row.partName && !row.signalName && (
                        <span style={{ fontSize: "0.75rem", color: dimText }}>No job assigned</span>
                    )}
                </div>

                {/* Count */}
                <div className="flex-shrink-0 text-right" style={{ minWidth: 90 }}>
                    {noData ? (
                        <span className="text-muted" style={{ fontSize: "0.8rem" }}>—</span>
                    ) : exp != null ? (
                        <>
                            <span className="font-weight-bold" style={{ fontSize: "0.9rem", color: isOver ? "#533f03" : color }}>
                                {actual.toLocaleString()}
                            </span>
                            <span style={{ fontSize: "0.72rem", color: dimText }}>
                                {" "}/ {exp.toLocaleString()}
                            </span>
                            {isOver && (
                                <span style={{
                                    marginLeft: 6,
                                    background: "#ffc107",
                                    color: "#212529",
                                    fontWeight: 700,
                                    fontSize: "0.68rem",
                                    padding: "1px 6px",
                                    borderRadius: 4,
                                    whiteSpace: "nowrap",
                                }}>
                                    +{(actual - exp).toLocaleString()} extra
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="font-weight-bold" style={{ fontSize: "0.9rem", color: boldText }}>
                            {actual.toLocaleString()}
                            <span style={{ fontSize: "0.7rem", color: dimText, fontWeight: 400, marginLeft: 3 }}>units</span>
                        </span>
                    )}
                </div>
            </div>

            {/* Bar */}
            <div style={{
                height: isOver ? 10 : 8,
                borderRadius: 4,
                background: isOver ? "rgba(255,193,7,0.22)" : "#e9ecef",
                overflow: "hidden",
            }}>
                {!noData && (exp != null || (actual || 0) > 0) && (
                    <div style={{
                        height: "100%",
                        borderRadius: 4,
                        width: exp != null ? `${pct}%` : "100%",
                        background: isOver
                            ? "repeating-linear-gradient(45deg,#ffc107,#ffc107 8px,#e6a800 8px,#e6a800 16px)"
                            : color,
                        transition: "width 0.4s ease",
                        minWidth: (actual || 0) > 0 ? 4 : 0,
                        opacity: exp == null ? 0.4 : 1,
                    }} />
                )}
            </div>

            {/* Sub-label */}
            {row.job && routeDurationSec(row.job) && (
                <div style={{ fontSize: "0.67rem", color: dimText, marginTop: 3 }}>
                    {moment.duration(routeDurationSec(row.job), "seconds").humanize()} / cycle
                    {exp != null && ` · ${exp} expected in shift`}
                </div>
            )}
            {row.partName && !routeDurationSec(row.job) && (
                <div style={{ fontSize: "0.67rem", color: "#adb5bd", marginTop: 3 }}>
                    No route duration set — expected count unavailable
                </div>
            )}
        </div>
    );
}

function ShiftProductionSummary({ deviceData, filters, range, isLive }) {
    const allShadowDevices = deviceData?.shadowDevices || [];

    const shadowDevices = useMemo(() => {
        if (!allShadowDevices.length || !filters?.devices) return allShadowDevices;
        const ids = filters.devices.split(",").map((d) => d.trim()).filter(Boolean);
        if (!ids.length) return allShadowDevices;
        return allShadowDevices.filter((dev) =>
            ids.includes(String(dev.id)) || ids.includes(dev.name),
        );
    }, [allShadowDevices, filters?.devices]);

    const timeRange = useMemo(() => {
        if (!filters?.durationStart || !filters?.durationEnd) return null;
        let start = filters.durationStart;
        let end = filters.durationEnd;
        if (Array.isArray(range) && range.length === 2) {
            const base = new Date(filters.durationStart);
            if (!isNaN(base.getTime())) {
                const [s, e] = range;
                start = new Date(base.getTime() + s * 1000).toISOString();
                end   = new Date(base.getTime() + e * 1000).toISOString();
            }
        }
        return { start, end };
    }, [filters, range]);

    const shiftDurationSec = filters?.durationSeconds || 0;

    const { data: productionDevices, loading } = useProductionData(shadowDevices, timeRange);

    const shiftWindow = useMemo(() => {
        if (!filters?.durationStart || !filters?.durationEnd) return null;
        return {
            start: moment(filters.durationStart).format("h:mma"),
            end:   moment(filters.durationEnd).format("h:mma"),
            date:  filters.shiftDate ? moment(filters.shiftDate).format("ddd, MMM D") : "",
        };
    }, [filters]);

    const groups = useMemo(() => {
        if (!productionDevices?.length) return [];

        const overlapSec = (startIso, endIso) => {
            if (!startIso || !endIso || !timeRange) return shiftDurationSec;
            const jS = Math.max(new Date(startIso).getTime(), new Date(timeRange.start).getTime());
            const jE = Math.min(new Date(endIso).getTime(),   new Date(timeRange.end).getTime());
            return Math.max(0, Math.floor((jE - jS) / 1000));
        };

        const inWindow = (startIso, endIso) => {
            if (!startIso || !endIso || !timeRange) return true;
            const jS = new Date(startIso).getTime();
            const jE = new Date(endIso).getTime();
            return jS < new Date(timeRange.end).getTime() && jE > new Date(timeRange.start).getTime();
        };

        const built = productionDevices.map((dev) => {
            const deviceName = dev.name || String(dev.id);
            const signals = dev.signals || [];
            const rows = [];

            if (!dev.productionCalculated || !signals.length) {
                rows.push({
                    key: `${dev.id}-no-signal`,
                    signalName: null,
                    isDigital: null,
                    partName: null,
                    progressLabel: null,
                    job: null,
                    effectiveDurSec: shiftDurationSec,
                    actual: dev.productionCalculated ? (dev.totalUnitsProduced || 0) : null,
                });
            } else {
                signals.forEach((sig) => {
                    const relevant = sig.jobBreakdown.filter((jb) => inWindow(jb.startTime, jb.endTime));
                    if (!relevant.length) {
                        rows.push({
                            key: `${dev.id}-${sig.name}-none`,
                            signalName: sig.name,
                            isDigital: sig.isDigital,
                            partName: null,
                            progressLabel: null,
                            job: null,
                            effectiveDurSec: shiftDurationSec,
                            actual: sig.totalUnitsProduced,
                        });
                    } else {
                        relevant.forEach((jb) => {
                            rows.push({
                                key: `${dev.id}-${sig.name}-${jb.jobId}`,
                                signalName: sig.name,
                                isDigital: sig.isDigital,
                                partName: jb.part?.name || null,
                                progressLabel: routeProgressLabel(jb),
                                job: jb,
                                effectiveDurSec: overlapSec(jb.startTime, jb.endTime),
                                actual: jb.unitsProduced,
                            });
                        });
                    }
                });
            }

            // Within a device: over-target rows first, then by actual desc
            rows.sort((a, b) => {
                const aOver = (expectedUnits(a.effectiveDurSec, a.job) ?? -1) < (a.actual || 0);
                const bOver = (expectedUnits(b.effectiveDurSec, b.job) ?? -1) < (b.actual || 0);
                if (aOver !== bOver) return aOver ? -1 : 1;
                return (b.actual || 0) - (a.actual || 0);
            });

            const hasOverTarget = rows.some((r) => {
                const exp = expectedUnits(r.effectiveDurSec, r.cycle);
                return exp != null && (r.actual || 0) > exp;
            });

            return { deviceId: dev.id, deviceName, rows, hasOverTarget };
        });

        // Devices with at least one over-target signal float to the top
        return built.sort((a, b) => {
            if (a.hasOverTarget !== b.hasOverTarget) return a.hasOverTarget ? -1 : 1;
            const aMax = Math.max(0, ...a.rows.map((r) => r.actual || 0));
            const bMax = Math.max(0, ...b.rows.map((r) => r.actual || 0));
            return bMax - aMax;
        });
    }, [productionDevices, timeRange, shiftDurationSec]);

    const totalActual = useMemo(
        () => groups.flatMap((g) => g.rows).reduce((s, r) => s + (r.actual || 0), 0),
        [groups],
    );
    const hasAnyData = groups.some((g) => g.rows.some((r) => (r.actual || 0) > 0));

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 160 }}>
                <Spinner />
            </div>
        );
    }

    if (!timeRange) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                    style={{ width: 48, height: 48, background: "rgba(0,115,183,0.08)" }}
                >
                    <i className="fa fa-calendar-times-o fa-lg text-muted" />
                </div>
                <p className="text-muted small mb-0">
                    {isLive ? "No shift is running right now." : "No completed shift found in the last 7 days."}
                </p>
            </div>
        );
    }

    return (
        <div>
            {/* Shift meta row */}
            <div className="d-flex align-items-center justify-content-between mb-3 pb-2"
                style={{ borderBottom: "1px solid #dee2e6" }}>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                    {isLive && (
                        <span className="badge badge-success" style={{ fontSize: "0.7rem" }}>
                            <i className="fa fa-circle mr-1" style={{ fontSize: "0.45rem", verticalAlign: "middle" }} />
                            Live
                        </span>
                    )}
                    {filters?.shiftName && (
                        <span className="badge badge-info" style={{ fontSize: "0.7rem" }}>{filters.shiftName}</span>
                    )}
                    {shiftWindow && (
                        <>
                            <span className="text-muted small">
                                <i className="fa fa-calendar-o mr-1" />{shiftWindow.date}
                            </span>
                            <span className="text-muted small">
                                <i className="fa fa-clock-o mr-1" />{shiftWindow.start}–{shiftWindow.end}
                            </span>
                        </>
                    )}
                </div>
                {hasAnyData && (
                    <span className="text-muted small flex-shrink-0">
                        <span className="font-weight-bold text-dark">{totalActual.toLocaleString()}</span>
                        {" "}units across all signals
                    </span>
                )}
            </div>

            {/* Grouped by device */}
            {groups.length === 0 ? (
                <div className="text-center text-muted py-4">
                    <i className="fa fa-inbox fa-2x mb-2 d-block" />
                    <p className="small mb-0">No devices or jobs found for this shift.</p>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {groups.map((group) => (
                        <div key={group.deviceId}>
                            {/* Device header */}
                            <div
                                className="d-flex align-items-center mb-2"
                                style={{
                                    borderBottom: "1px solid #dee2e6",
                                    paddingBottom: 5,
                                    gap: 8,
                                }}
                            >
                                <i className="fa fa-microchip" style={{ fontSize: "0.78rem", color: "#6c757d" }} />
                                <span className="font-weight-bold" style={{ fontSize: "0.85rem", color: "#343a40" }}>
                                    {group.deviceName}
                                </span>
                                <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                                    {group.rows.length} {group.rows.length === 1 ? "signal" : "signals"}
                                </span>
                                {group.hasOverTarget && (
                                    <span style={{ marginLeft: "auto" }}>
                                        <i className="fa fa-star" style={{ color: "#e6a800", fontSize: "0.72rem" }} />
                                        <i className="fa fa-star ml-1" style={{ color: "#e6a800", fontSize: "0.72rem" }} />
                                        <i className="fa fa-star ml-1" style={{ color: "#e6a800", fontSize: "0.72rem" }} />
                                    </span>
                                )}
                            </div>

                            {/* Signal rows */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingLeft: 12 }}>
                                {group.rows.map((row) => (
                                    <SignalRow key={row.key} row={row} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default ShiftProductionSummary;
